import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type PagedResult,
  type ProjectFolderDetail,
  type ProjectFolderWrite,
  type StructureListItem,
} from '../lib/api';
import { ProjectCostingPanel } from './ProjectCostingPanel';
import { ProjectShareCard } from './ProjectShareCard';
import { AdminAgentNotesCard } from '../lib/AdminAgentNotesCard';

/**
 * Project folder detail — mirrors `.old/resources/views/project_folder/view.blade.php`.
 */
export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = Number(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const projectQuery = useQuery({
    queryKey: ['project-folder', projectId],
    queryFn: () => api.get<ProjectFolderDetail>(`/api/v1/project-folders/${projectId}`),
    enabled: !Number.isNaN(projectId),
  });

  if (projectQuery.isLoading) {
    return (
      <Shell>
        <p className="text-muted">Loading…</p>
      </Shell>
    );
  }
  if (projectQuery.error || !projectQuery.data) {
    return (
      <Shell>
        <div className="alert alert-danger">Project not found.</div>
      </Shell>
    );
  }

  const project = projectQuery.data;
  const shouldOpenPicker = searchParams.get('add') === '1';

  return (
    <Shell>
      <div className="row mt-3 align-items-end">
        <div className="col-md-8">
          <Link to="/admin/projects" className="small text-muted">
            ← Projects
          </Link>
          <h4 className="page-title mb-1">{project.name}</h4>
          <p className="text-muted small mb-0">
            {project.city}, {project.state}
            {project.userName ? ` · owner: ${project.userName}` : ''}
          </p>
        </div>
        <div className="col-md-4 text-md-end mt-2 mt-md-0">
          <a
            href={project.structures.length > 0 ? `/project/pdf/${projectId}` : undefined}
            target={project.structures.length > 0 ? '_blank' : undefined}
            rel={project.structures.length > 0 ? 'noopener' : undefined}
            className={`btn btn-dark me-2 ${project.structures.length === 0 ? 'disabled' : ''}`}
            aria-disabled={project.structures.length === 0}
            onClick={(event) => {
              if (project.structures.length === 0) {
                event.preventDefault();
                alert('Add at least one structure before downloading the project PDF.');
              }
            }}
          >
            Download PDF
          </a>
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={async () => {
              if (!confirm('Delete this project folder?')) return;
              await api.del(`/api/v1/project-folders/${projectId}`);
              queryClient.invalidateQueries({ queryKey: ['project-folders'] });
              navigate('/admin/projects');
            }}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-xl-6">
          <ProjectInfoCard project={project} />
        </div>
        <div className="col-12 col-xl-6">
          <StructuresCard project={project} initiallyOpen={shouldOpenPicker} />
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <ProjectCostingPanel projectId={project.id} />
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <ProjectShareCard projectId={project.id} />
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <AdminAgentNotesCard
            kind="projects"
            id={project.id}
            initialQuoteCompleted={project.quoteCompleted}
          />
        </div>
      </div>
    </Shell>
  );
}

function ProjectInfoCard({ project }: { project: ProjectFolderDetail }) {
  const [form, setForm] = useState<ProjectFolderWrite>({
    name: project.name,
    city: project.city,
    state: project.state,
    designEngineer: project.designEngineer,
    siteContractor: project.siteContractor,
    additionalNotes: project.additionalNotes,
  });
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const m = useMutation({
    mutationFn: () => api.put<ProjectFolderDetail>(`/api/v1/project-folders/${project.id}`, form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-folder', project.id] });
      queryClient.invalidateQueries({ queryKey: ['project-folders'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Save failed'),
  });

  const set = <K extends keyof ProjectFolderWrite>(k: K, v: ProjectFolderWrite[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h4 className="header-title mb-0">Details</h4>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={m.isPending}
          onClick={() => {
            setError(null);
            m.mutate();
          }}
        >
          {m.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      <div className="card-body">
        <div className="mb-3">
          <label className="form-label">Name</label>
          <input
            className="form-control"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </div>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label">City</label>
            <input
              className="form-control"
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">State</label>
            <input
              className="form-control"
              value={form.state}
              onChange={(e) => set('state', e.target.value)}
            />
          </div>
        </div>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label">Design engineer</label>
            <input
              className="form-control"
              value={form.designEngineer ?? ''}
              onChange={(e) => set('designEngineer', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Site contractor</label>
            <input
              className="form-control"
              value={form.siteContractor ?? ''}
              onChange={(e) => set('siteContractor', e.target.value)}
            />
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">Additional notes</label>
          <textarea
            className="form-control"
            rows={3}
            value={form.additionalNotes ?? ''}
            onChange={(e) => set('additionalNotes', e.target.value)}
          />
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        <p className="text-muted small mb-0">
          Created {fmt(project.createdAt)} · Updated {fmt(project.updatedAt)}
        </p>
      </div>
    </div>
  );
}

function StructuresCard({
  project,
  initiallyOpen = false,
}: {
  project: ProjectFolderDetail;
  initiallyOpen?: boolean;
}) {
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(initiallyOpen);
  const [quantities, setQuantities] = useState<Record<number, number>>(() =>
    Object.fromEntries(project.structures.map((structure) => [structure.id, structure.quantity])),
  );

  const removeMutation = useMutation({
    mutationFn: (structureId: number) =>
      api.del(`/api/v1/project-folders/${project.id}/structures/${structureId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-folder', project.id] }),
  });

  const updateQuantityMutation = useMutation({
    mutationFn: ({ structureId, quantity }: { structureId: number; quantity: number }) =>
      api.post(`/api/v1/project-folders/${project.id}/structures`, {
        structureId,
        quantity: Math.max(1, quantity),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-folder', project.id] }),
  });

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h4 className="header-title mb-0">Structures · {project.structures.length}</h4>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => setPickerOpen((v) => !v)}
        >
          {pickerOpen ? 'Cancel' : '+ Add'}
        </button>
        <Link to="/newstructure" className="btn btn-sm btn-primary ms-2">
          + Create New Structure
        </Link>
      </div>
      <div className="card-body">
        {pickerOpen && (
          <div className="border-bottom pb-3 mb-3">
            <StructurePicker
              projectId={project.id}
              existingIds={new Set(project.structures.map((s) => s.id))}
              onAdded={() => {
                queryClient.invalidateQueries({ queryKey: ['project-folder', project.id] });
                setPickerOpen(false);
              }}
            />
          </div>
        )}

        {project.structures.length === 0 ? (
          <p className="text-muted mb-0">No structures yet.</p>
        ) : (
          <ul className="list-group list-group-flush">
            {project.structures.map((s) => (
              <li
                key={s.id}
                className="list-group-item d-flex justify-content-between align-items-center px-0"
              >
                <div className="min-w-0">
                  <Link to={`/admin/structures/${s.id}`} className="fw-semibold">
                    #{s.id} {s.nickname ?? '(unnamed)'}
                  </Link>
                  <div className="text-muted small">
                    {s.structureTypeName ?? '—'}
                    {s.foundryName ? ` · ${s.foundryName}` : ''}
                    {' · '}
                    {s.permanent ? 'Permanent' : 'Temporary'}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    min={1}
                    value={quantities[s.id] ?? s.quantity}
                    aria-label={`Quantity for ${s.nickname ?? `structure ${s.id}`}`}
                    style={{ width: 76 }}
                    onChange={(event) => setQuantities((current) => ({
                      ...current,
                      [s.id]: Math.max(1, Number(event.target.value) || 1),
                    }))}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={updateQuantityMutation.isPending}
                    onClick={() => updateQuantityMutation.mutate({
                      structureId: s.id,
                      quantity: quantities[s.id] ?? s.quantity,
                    })}
                  >
                    Update
                  </button>
                  {s.isDraft && <span className="badge bg-warning">Draft</span>}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeMutation.mutate(s.id)}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StructurePicker({
  projectId,
  existingIds,
  onAdded,
}: {
  projectId: number;
  existingIds: Set<number>;
  onAdded: () => void;
}) {
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [addingId, setAddingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['structures', { all: false, picker: true }],
    queryFn: () => api.get<PagedResult<StructureListItem>>('/api/v1/structures?pageSize=200'),
  });

  const m = useMutation({
    mutationFn: (structureId: number) => {
      setAddingId(structureId);
      return api.post(`/api/v1/project-folders/${projectId}/structures`, {
        structureId,
        quantity: Math.max(1, quantities[structureId] ?? 1),
      });
    },
    onSuccess: onAdded,
    onError: (err) => {
      setAddingId(null);
      setError(err instanceof Error ? err.message : 'Failed');
    },
  });

  const options = (list.data?.items ?? []).filter((s) => !existingIds.has(s.id));

  return (
    <div>
      {list.isLoading && <p className="text-muted mb-0">Loading available structures…</p>}
      {!list.isLoading && options.length === 0 && (
        <p className="text-muted mb-0">All of your saved structures are already in this project.</p>
      )}
      {options.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Structure Name</th>
                <th>Type</th>
                <th>Style Number</th>
                <th style={{ width: 110 }}>Quantity</th>
                <th style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {options.map((structure) => {
                const quantity = quantities[structure.id] ?? 1;
                return (
                  <tr key={structure.id}>
                    <td>{structure.nickname ?? `Structure #${structure.id}`}</td>
                    <td>{structure.structureTypeName ?? '—'}</td>
                    <td>{structure.styleNumber ?? structure.foundryPartNumber ?? '—'}</td>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        min={1}
                        value={quantity}
                        aria-label={`Quantity for ${structure.nickname ?? `structure ${structure.id}`}`}
                        onChange={(event) => setQuantities((current) => ({
                          ...current,
                          [structure.id]: Math.max(1, Number(event.target.value) || 1),
                        }))}
                      />
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success"
                        disabled={m.isPending}
                        onClick={() => {
                          setError(null);
                          m.mutate(structure.id);
                        }}
                      >
                        {addingId === structure.id ? 'Adding…' : '+ Add'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {error && <div className="text-danger small mt-2">{error}</div>}
    </div>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return <div className="container-fluid">{children}</div>;
}

function fmt(iso: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}
